// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { PurchaseOrder, purchaseOrderSchema } from '@/purchase-order/api/types';
import {
  PHASE_CONFIG,
  POPhase,
  POSubstatus,
  PurchaseOrderFilter,
  poPhaseSchema,
  poSubstatusSchema,
} from '@/purchase-order/constants/status';
import type { PurchaseOrderSortProperty } from '@/purchase-order/hooks/use-purchase-orders';
import queryString from 'query-string';
import { z } from 'zod';

const SORT_PROPERTY_TO_BACKEND: Record<PurchaseOrderSortProperty, string> = {
  poNumber: 'purchaseOrderNumber',
  status: 'status',
};

/**
 * Type definition for the paginated response.
 */
interface PagedPurchaseOrdersResponse {
  results: unknown[];
  totalCount: number;
}

/**
 * Type definition for the API response wrapper.
 */
interface ApiResponse {
  data: PagedPurchaseOrdersResponse;
  metadata: { requestId: string };
}

/**
 * Type definition for the get purchase orders API response.
 */
interface PurchaseOrdersResponse {
  purchaseOrders: PurchaseOrder[];
  resultCount: number;
}

export function getFilterStatuses(
  status?: POPhase,
  subStatuses?: POSubstatus[]
): string[] | undefined {
  if (status === undefined && subStatuses === undefined) {
    return undefined;
  }

  const phases: readonly POPhase[] =
    status !== undefined ? [status] : poPhaseSchema.options;

  const allFilters = phases.flatMap((p) => PHASE_CONFIG[p].backendFilters);

  if (subStatuses === undefined || subStatuses.length === 0) {
    return allFilters;
  }

  const suffixes = new Set(subStatuses.map((m) => m.toUpperCase()));
  const allModifiers = new Set(
    poSubstatusSchema.options.map((m) => m.toUpperCase())
  );
  return allFilters.filter((s) => {
    const suffix = s.split('_').pop();
    if (suffix === undefined || !allModifiers.has(suffix)) {
      return true;
    }
    return suffixes.has(suffix);
  });
}

/**
 * Fetches purchase orders with the specified filter criteria.
 */
export async function getPurchaseOrders(
  filter?: PurchaseOrderFilter
): Promise<PurchaseOrdersResponse> {
  const url = '/v1/purchase-order';
  const response = await api.get<ApiResponse>(url, {
    paramsSerializer: (parameters) => queryString.stringify(parameters),
    params: {
      statuses: getFilterStatuses(filter?.status, filter?.subStatuses),
      search: filter?.searchTerm,
      page: filter?.page,
      limit: filter?.pageSize,
      fromLocationIds: filter?.fromLocationIds,
      fromCustomerIds: filter?.fromCustomerIds,
      fromLocationCompanyTypes: filter?.fromLocationCompanyTypes,
      toLocationIds: filter?.toLocationIds,
      toCustomerIds: filter?.toCustomerIds,
      toLocationCompanyTypes: filter?.toLocationCompanyTypes,
      sortBy: filter?.sort
        ? SORT_PROPERTY_TO_BACKEND[filter.sort.property]
        : undefined,
      sortOrder: filter?.sort?.direction,
    },
  });

  // Parse and validate each purchase order with Zod
  const purchaseOrdersSchema = z.array(purchaseOrderSchema);
  const purchaseOrders = await parseResponse(
    purchaseOrdersSchema,
    response.data.data.results,
    {
      schemaName: 'PurchaseOrders',
      endpoint: url,
    }
  );

  return {
    purchaseOrders,
    resultCount: response.data.data.totalCount,
  };
}
